import { useCallback, useRef, useState } from "react";
import { SEARCH_PAGE_SIZE_OPTIONS } from "../Search/SearchResultsPaginationModel";

/** FE #516: the stored snapshot pages server-side; 100 is the backend's largest page. */
export const SNAPSHOT_PAGE_SIZE = 100;
const SMALLEST_PAGE_SIZE = Math.min(...SEARCH_PAGE_SIZE_OPTIONS);

/**
 * Which stored-snapshot page the saved-search grid shows. The page resets to 1 when the
 * route id changes. `requested()` is read synchronously by the detail fetch so refreshes
 * (batch settle, return to snapshot) reload the page the user is on.
 */
export function useSavedSearchSnapshotPager(id) {
  const [pager, setPager] = useState({ id, page: 1, pageSize: SNAPSHOT_PAGE_SIZE });
  const current = pager.id === id ? pager : { id, page: 1, pageSize: SNAPSHOT_PAGE_SIZE };
  const pagerRef = useRef(current);
  if (pagerRef.current.id !== id) pagerRef.current = current;

  const requested = useCallback(() => pagerRef.current, []);
  const goTo = useCallback((page, pageSize) => {
    const next = { id, page, pageSize };
    pagerRef.current = next;
    setPager(next);
  }, [id]);

  return { page: current.page, pageSize: current.pageSize, requested, goTo };
}

/** Props for SearchResultsPagination, or null when one page holds everything. */
export function snapshotPaginationProps({ pager, totalRows, disabled, onChange }) {
  if (pager.page <= 1 && totalRows <= SMALLEST_PAGE_SIZE) return null;
  return {
    page: pager.page - 1,
    rowsPerPage: pager.pageSize,
    totalRows,
    lastReachablePage: Math.max(0, Math.ceil(totalRows / pager.pageSize) - 1),
    disabled,
    onPageChange: (pageIndex) => onChange(pageIndex + 1, pager.pageSize),
    onRowsPerPageChange: (pageSize) => onChange(1, pageSize),
  };
}
